import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

const NOT_COUNTRIES = new Set(["AC", "CP", "CQ", "DG", "EA", "EU", "EZ", "IC", "QO", "TA", "UN", "XA", "XB"]);
const FIRST = ["United Kingdom", "United States", "France", "Germany", "Switzerland", "Ireland"];

function buildCountries() {
  const names = new Intl.DisplayNames(["en-GB"], { type: "region" });
  const out = [];
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a) + String.fromCharCode(b);
      if (NOT_COUNTRIES.has(code)) continue;
      const name = names.of(code);
      if (name && name !== code) out.push(name);
    }
  }
  out.sort((x, y) => x.localeCompare(y, "en-GB"));
  return [...FIRST, ...out.filter((n) => !FIRST.includes(n))];
}

// Searchable country picker. Stores the English country name so the
// VAT rule and the Cognito address field keep working unchanged.
export function CountrySelect({ value, onChange, testid, invalid = false }) {
  const [open, setOpen] = useState(false);
  const countries = useMemo(buildCountries, []);
  const current = (value || "").trim();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className={`h-11 w-full rounded-md border bg-white px-3.5 text-[15px] text-left flex items-center justify-between gap-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.03)] transition-[border-color,box-shadow] focus:outline-none focus:border-[#1B4F8A] focus:ring-4 focus:ring-[#1B4F8A]/10 ${invalid ? "border-red-500 ring-4 ring-red-500/10" : "border-stone-300"} ${current ? "text-stone-900" : "text-stone-400"}`}
        >
          <span className="truncate">{current || "Select a country"}</span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 text-stone-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command filter={(item, search) => (item.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0)}>
          <CommandInput placeholder="Type to search" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((name) => (
                <CommandItem
                  key={name}
                  value={name}
                  onSelect={() => { onChange(name); setOpen(false); }}
                  data-testid={testid ? `${testid}-option-${name.toLowerCase().replace(/[^a-z]+/g, "-")}` : undefined}
                >
                  <Check className={`w-4 h-4 mr-2 ${name === current ? "opacity-100 text-[#1B4F8A]" : "opacity-0"}`} />
                  {name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
